import Bugsnag from '@bugsnag/js'
import styled from '@emotion/styled'
import type { TFunction } from 'i18next'

import { CardItem, type LinkAdapterProps } from '@cash-web/support/ui-components'

import MoreResultsButton from './MoreResultsButton'
import ResultCards from './ResultCards'
import type { SearchResult } from './types'

const ResultsContainer = styled.div`
  position: relative;
`

interface SearchResultsProps<T> extends LinkAdapterProps<T> {
  results: SearchResult[]
  query: string
  showMoreResults?: boolean
  isErrored?: boolean
  errorMessage?: string
  t: TFunction
  fullScreenMode?: boolean
}

export const SearchResults = <T,>({
  results,
  query,
  showMoreResults = false,
  isErrored = false,
  errorMessage = '',
  Link,
  t,
  fullScreenMode = false,
}: SearchResultsProps<T>) => {
  if (isErrored) {
    const message = errorMessage.length === 0 ? 'unknown error' : errorMessage
    Bugsnag.notify(new Error(`Help: search error "${message}", with query length "${query.length}"`))
    return (
      <CardItem
        truncate={false}
        title={t('Error with your search query', { context: 'search error title' })}
        subtitle={t('We had trouble with your search. Please try again later.', { context: 'search error subtitle' })}
      />
    )
  }

  if (results.length) {
    return (
      <>
        <ResultsContainer>
          {<ResultCards fullScreenMode={fullScreenMode} results={results} query={query} />}
        </ResultsContainer>
        {showMoreResults ? <MoreResultsButton query={query} Link={Link} t={t} /> : undefined}
      </>
    )
  }

  return (
    <CardItem
      truncate={false}
      title={t('No results found', { context: 'search no results title' })}
      subtitle={t('Please try using other search terms or try more general keywords. Or you can try contacting us.', {
        context: 'search no results subtitle',
      })}
    />
  )
}

export default SearchResults
